import { useState, useEffect, useContext } from 'react';
import { AuthContext } from "../../../auth/Auth";
import { CollectionInterface } from 'ts-interfaces';
import { useMattoState } from '../../../MattoState';
import { associateMultipleCollectionsWithProject } from '../../../v2/apis/collectionsSchemeApi';
import { useCollections } from '../../../global-state/useCollections';

export const useCollectionManagement = (
  projectId: string,
  collections: CollectionInterface[],
  setCollections: (projectId: string, collections: CollectionInterface[], userId: string, permissions: any) => Promise<void>,
  permissions: any,
  setSnackbarMessage: (message: string) => void,
  associatedProjectsList: any[]
) => {
  const { currentUser } = useContext(AuthContext);
  const [openCollectionModal, setOpenCollectionModal] = useState(false);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<Set<string>>(new Set());
  const [openCollectionChoiceModal, setOpenCollectionChoiceModal] = useState(false);
  const [initialSelectedCollectionIds, setInitialSelectedCollectionIds] = useState<Set<string>>(new Set());

  // Initialize selected collection IDs when collections change
  useEffect(() => {
    if (collections && collections.length > 0) {
      const currentCollectionIds = new Set(collections.map(collection => collection.id));
      setSelectedCollectionIds(currentCollectionIds);
      setInitialSelectedCollectionIds(currentCollectionIds);
    }
  }, [collections]);

  const handleOpenCollectionModal = () => {
    setOpenCollectionChoiceModal(true);
  };

  const handleCloseCollectionModal = () => {
    setOpenCollectionModal(false);
    // Reset selections to current state when closing without saving
    setSelectedCollectionIds(new Set(collections.map(collection => collection.id)));
  };

  const handleCloseCollectionChoiceModal = () => {
    setOpenCollectionChoiceModal(false);
  };

  const handleAddExistingCollection = () => {
    setOpenCollectionChoiceModal(false);
    setOpenCollectionModal(true);
  };

  const handleCreateNewCollection = async () => {
    setOpenCollectionChoiceModal(false);
    useMattoState.getState().setModal({
      name: 'collection',
      id: null,
      associatedProjectsList: associatedProjectsList,
      associatedProjectId: projectId,
      onSave: (collection: CollectionInterface) => handleCollectionUpdate(collection, true)
    });
  };

  const handleCollectionSelectionChange = (collectionId: string, isChecked: boolean) => {
    const newSelectedIds = new Set(selectedCollectionIds);
    if (isChecked) {
      newSelectedIds.add(collectionId);
    } else {
      newSelectedIds.delete(collectionId);
    }
    setSelectedCollectionIds(newSelectedIds);
  };

  const handleSaveCollectionAssociations = async () => {
    setSnackbarMessage('Updating collection associations...');
    try {
      // Get all available collections including both user collections and shared collections
      const collectionsState = useCollections.getState();
      const allAvailableCollections = [
        ...collectionsState.getOwnedCollections(),
        ...collectionsState.getSharedCollections()
      ];
      
      // Get collections that were initially selected (project's current collections)
      const initiallySelected = Array.from(initialSelectedCollectionIds);
      
      // Get collections that are currently selected
      const currentlySelected = Array.from(selectedCollectionIds);
      
      // Find collections to add (newly selected)
      const collectionsToAdd = currentlySelected.filter(id => !initiallySelected.includes(id));
      
      // Find collections to remove (were selected, now unselected)
      const collectionsToRemove = initiallySelected.filter(id => !currentlySelected.includes(id));
      
      await associateMultipleCollectionsWithProject(collectionsToAdd, collectionsToRemove, projectId, currentUser.uid, permissions);

      // Get the actual collection objects for collections to add
      const newCollectionsToAdd = allAvailableCollections.filter(collection => 
        collectionsToAdd.includes(collection.id)
      );
      
      // Start with current project collections
      let updatedCollections = [...collections];
      
      // Remove collections that the user deselected
      updatedCollections = updatedCollections.filter(collection => !collectionsToRemove.includes(collection.id));
      
      // Add newly selected collections
      updatedCollections = [...updatedCollections, ...newCollectionsToAdd];
      
      // Remove duplicates (in case of any edge cases)
      const uniqueCollections = updatedCollections.filter((collection, index, self) => 
        index === self.findIndex(c => c.id === collection.id)
      );
      
      await setCollections(projectId, uniqueCollections, currentUser.uid, permissions);
      collectionsState.syncCollectionAssociations(collectionsToAdd, collectionsToRemove, projectId);

      setSnackbarMessage('Collection associations updated successfully');
      handleCloseCollectionModal();
      
      // Update the initial state for next time
      setInitialSelectedCollectionIds(new Set(uniqueCollections.map(c => c.id)));
      setSelectedCollectionIds(new Set(uniqueCollections.map(c => c.id)));
    } catch (error) {
      console.error('Error updating collection associations:', error);
      setSnackbarMessage('Failed to update collection associations');
    }
  };

  const handleCollectionUpdate = (collection: CollectionInterface, addCollection = false, isDeleted = false) => {
    if (addCollection) {
      setSnackbarMessage('Creating new collection...');
      setCollections(projectId, [collection, ...collections], currentUser.uid, permissions);
      setSnackbarMessage('New collection created successfully');
    } else if (isDeleted) {
      setSnackbarMessage('Deleting collection...');
      const updatedCollections = collections.filter(b => b.id !== collection.id);
      setCollections(projectId, updatedCollections, currentUser.uid, permissions);
      setSnackbarMessage('Collection deleted successfully');
    } else {
      setSnackbarMessage('Saving collection updates...');
      const updatedCollections = collections.map(b => b.id === collection.id ? collection : b);
      setCollections(projectId, updatedCollections, currentUser.uid, permissions);
      setSnackbarMessage('Collection updates saved successfully');
    }
  };

  return {
    // State
    openCollectionModal,
    selectedCollectionIds,
    openCollectionChoiceModal,
    
    // Actions
    handleOpenCollectionModal,
    handleCloseCollectionModal,
    handleCloseCollectionChoiceModal,
    handleAddExistingCollection,
    handleCreateNewCollection,
    handleCollectionSelectionChange,
    handleSaveCollectionAssociations,
    handleCollectionUpdate
  };
};